import { Form, message } from "antd";
import { ReusableModal } from "@/components/UI/Modals/CustomModals";
import { useState, useMemo } from "react";

import { useGetAllCustomersQuery } from "@/services/api/user/customerApi";
import {
  useGetAllProductQuery,
  useLazyGetProductByIdQuery,
} from "@/services/api/product/productApi";
import { useCreateBulkOrderMutation } from "@/services/api/orders/ordersApi";
import { useGetLocationsQuery } from "@/services/api/location/locationApi";
import { useGetDeliveryModesQuery } from "@/services/api/deliveryMode/deliveryModeApi"; // Import this
import DeliverySection from "../../component/deliverySection";
import SelectCustomer from "../sections/SelectCustomer";
import SelectProducts from "../sections/SelectProducts";
import ReviewBulkOrder from "../sections/ReviewBulkOrder";
import CreateCustomerModal from "@/pages/Admin/Customers/modals/CreateCustomerModal";

const CreateBulkOrderModal = ({ open, onClose }) => {
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [discount, setDiscount] = useState("");
  const [orderItems, setOrderItems] = useState([]);
  const [isCreateCustomerOpen, setIsCreateCustomerOpen] = useState(false);
  const [customerSearch, setCustomerSearch] = useState("");
  // Removed: deliveryType and setDeliveryType as DeliverySection now manages this internally
  const [discountType, setDiscountType] = useState("percentage");

  // API Queries
  const { data: customersResponse, isLoading: customerLoading } =
    useGetAllCustomersQuery(undefined, { skip: !open });
  const { data: locations = [], isLoading: locationLoading } =
    useGetLocationsQuery(undefined, { skip: !open });
  const { data: productsResponse, isLoading: productsLoading } =
    useGetAllProductQuery(undefined, { skip: !open });
  const {
    data: deliveryModes,
    isLoading: isDeliveryModesLoading,
  } = // Fetch delivery modes here
    useGetDeliveryModesQuery(undefined, { skip: !open });
  const [fetchProductById] = useLazyGetProductByIdQuery({ skip: !open });
  const [createBulkOrder, { isLoading: creatingBulkOrder }] =
    useCreateBulkOrderMutation();

  // Transform customer data
  const customerOptions = useMemo(() => {
    if (!customersResponse?.data) return [];
    return customersResponse.data.map((customer) => ({
      value: customer.id,
      label: `${customer.first_name || ""} ${customer.last_name || ""}`.trim(),
    }));
  }, [customersResponse]);

  // Transform location data for branch dropdown
  const locationOptions = useMemo(
    () =>
      locations.map((loc) => ({
        value: loc.id,
        label: `${loc.name} - ${loc.state?.name || ""}`,
      })),
    [locations],
  );

  // Transform product data - simplified
  const productDropdownOptions = useMemo(() => {
    const productsArray =
      productsResponse?.data?.products ||
      productsResponse?.data ||
      productsResponse?.products ||
      productsResponse;

    if (!Array.isArray(productsArray)) return [];

    return productsArray.map((product) => ({
      value: product.id,
      label: product.name || product.product_name || `Product ${product.id}`,
    }));
  }, [productsResponse]);

  const handleClose = () => {
    form.resetFields();
    setCurrentStep(0);
    setOrderItems([]);
    setDiscount("");
    // deliveryType state is removed here
    onClose();
  };

  const steps = [
    { title: "Choose Customer", description: "" },
    { title: "Add Order", description: "" },
    { title: "Delivery Details", description: "" },
    { title: "Review Order", description: "" },
  ];

  const bulkOrderStepStyles = {
    activeColor: "#F9A022",
    finishedColor: "#F9A022",
    waitingColor: "#E5E7EB",
    activeTextColor: "#F9A022",
    finishedTextColor: "#F9A022",
    waitingTextColor: "#9CA3AF",
    connectorColor: "#E5E7EB",
  };

  const addOrderItem = () => {
    setOrderItems([
      ...orderItems,
      {
        id: Date.now(),
        productId: null,
        productName: "",
        productImage: null,
        quantity: 1,
        servingSize: 1,
        unit: "unit",
        price: 0,
        subtotal: 0,
      },
    ]);
  };

  const removeOrderItem = (itemId) => {
    setOrderItems(orderItems.filter((item) => item.id !== itemId));
  };

  // Simplified product selection handler
  const handleProductSelect = async (itemId, productId) => {
    if (!productId) return;

    try {
      const response = await fetchProductById(productId).unwrap();
      const productData = response?.data || response;

      // Extract product details - check both nested and flat structures
      const original = productData?.original || productData;
      const price = parseFloat(original?.amount || original?.price || 0);
      const servingSize = original?.serving_size || 1;
      const unit =
        original?.unit_of_measurement?.label || original?.unit || "unit";
      const productName = original?.name || productData?.name || "";
      const minimumQuantity = original?.minimum_qty || 1;
      const currentQuantity = original?.current_qty || 0; // Changed default to 0
      const productImage = original?.image?.url || null;

      // ✅ CHECK OUT OF STOCK IMMEDIATELY AFTER FETCHING
      if (currentQuantity === 0) {
        message.error(
          `${
            productName || "This product"
          } is out of stock. Please select another product.`,
          3,
        );
        // Still update the item but with quantity set to 0
      }

      setOrderItems((prevItems) =>
        prevItems.map((item) => {
          if (item.id === itemId) {
            return {
              ...item,
              productId: parseInt(productId) || "",
              productName,
              productImage,
              price,
              servingSize,
              unit,
              minimumQuantity,
              currentQuantity,
              // Set quantity to 0 if out of stock
              quantity:
                currentQuantity === 0 ? 0 : item.quantity || minimumQuantity,
              subtotal:
                currentQuantity === 0
                  ? 0
                  : price * (item.quantity || minimumQuantity),
            };
          }
          return item;
        }),
      );
    } catch (error) {
      // console.error("Error fetching product details:", error);
      message.error("Failed to load product details");
    }
  };

  const handleQuantityChange = (itemId, inputString) => {
    const parsedQuantity = parseInt(inputString, 10);

    setOrderItems((prevItems) =>
      prevItems.map((item) => {
        if (item.id === itemId) {
          const subtotal = item.price * parsedQuantity;

          return {
            ...item,
            quantity: parsedQuantity,
            subtotal: subtotal,
          };
        }
        return item;
      }),
    );
  };

  const calculateTotal = () => {
    return orderItems.reduce((sum, item) => sum + (item.subtotal || 0), 0);
  };

  const calculateReviewData = () => {
    const subtotal = calculateTotal();
    let discountAmount = 0;
    let discountText = "0%";

    if (discount) {
      if (discountType === "percentage") {
        const percent = parseFloat(discount);
        discountAmount = (subtotal * percent) / 100;
        discountText = `${percent}%`;
      } else {
        discountAmount = parseFloat(discount) || 0;
        discountText = `₦${discountAmount.toLocaleString()}`;
      }
    }

    return {
      subtotal,
      discount: discountText,
      discountAmount,
      total: subtotal - discountAmount,
    };
  };

  const handleNext = async (step) => {
    try {
      if (step === 0) {
        await form.validateFields(["customer"]);
      }
      if (step === 1) {
        const validItems = orderItems.filter(
          (item) => item.productId && item.quantity > 0,
        );
        if (validItems.length === 0) {
          message.error("Please add at least one product");
          return;
        }
      }
      // If moving from Delivery Details, validate its fields
      if (step === 2) {
        const deliveryModeId = form.getFieldValue("deliveryModeId");
        const selectedMode = deliveryModes?.find(
          (mode) => mode.id === deliveryModeId,
        );

        if (!selectedMode) {
          message.error("Please select a delivery mode.");
          return;
        }

        const isDelivery = selectedMode.name.toLowerCase().includes("delivery");
        const isPickup = selectedMode.name.toLowerCase().includes("pickup");

        if (isDelivery) {
          await form.validateFields([
            "deliveryDate",
            "deliveryTime", // Now always required for delivery
            "deliveryLocation",
            "branch",
            "deliveryModeId", // Ensure the delivery mode itself is validated
          ]);
        } else if (isPickup) {
          await form.validateFields([
            "pickupDate",
            "pickupTime",
            "branch",
            "deliveryModeId", // Ensure the delivery mode itself is validated
          ]);
        } else {
          // Fallback if mode type is unknown but a mode is selected
          await form.validateFields(["branch", "deliveryModeId"]);
        }
      }
      setCurrentStep(step + 1);
    } catch (error) {
      console.log("Validation failed:", error);
    }
  };

  const handlePrevious = (step) => {
    setCurrentStep(step - 1);
  };

  const handleFinish = async () => {
    try {
      const validOrderItems = orderItems.filter(
        (item) => item.productId && item.quantity > 0,
      );

      if (validOrderItems.length === 0) {
        message.error("Please add at least one product to the order");
        return;
      }

      // Get selected delivery mode from the form
      const deliveryModeId = form.getFieldValue("deliveryModeId");
      const selectedMode = deliveryModes?.find(
        (mode) => mode.id === deliveryModeId,
      );

      if (!selectedMode) {
        message.error("Please select a valid delivery mode.");
        return;
      }

      const isDelivery = selectedMode.name.toLowerCase().includes("delivery");
      const isPickup = selectedMode.name.toLowerCase().includes("pickup");

      // Validate based on the selected mode
      if (isDelivery) {
        await form.validateFields([
          "deliveryDate",
          "deliveryTime",
          "deliveryLocation",
          "branch",
        ]);
      } else if (isPickup) {
        await form.validateFields(["pickupDate", "pickupTime", "branch"]);
      } else {
        message.error("Invalid delivery mode selected.");
        return;
      }

      const reviewData = calculateReviewData();
      // I need to check out this part again, I think there is a mistake in the payload, the delivery mode should be sent as an id not a name, and also the delivery and pickup times should be sent in a specific format, I will check with the backend team and get back to you on this.
      const orderPayload = {
        user_id: form.getFieldValue("customer"),
        discount_value: parseFloat(reviewData.discountAmount.toFixed(2)),
        sub_total: parseFloat(reviewData.total.toFixed(2)),

        // Set delivery mode ID directly
        delivery_mode: deliveryModeId,

        // Conditional fields based on delivery type
        delivery_date: isDelivery
          ? form.getFieldValue("deliveryDate")?.format("YYYY-MM-DD")
          : null,
        // delivery_time: isDelivery
        //   ? form.getFieldValue("deliveryTime") // TimePicker's normalize already formats it
        //   : null,
        delivery_address: isDelivery
          ? form.getFieldValue("deliveryLocation")
          : null,

        pick_up_date: isPickup
          ? form.getFieldValue("pickupDate")?.format("YYYY-MM-DD")
          : null,
        pick_up_time: isPickup
          ? form.getFieldValue("pickupTime") // TimePicker's normalize already formats it
          : null,

        // Common fields
        packaging_option: isPickup ? "packaged" : "standard", // Adjust if your backend needs more specific logic here
        is_gift: false, // Defaulting to false, adjust if you add this feature
        gifting_address: null, // Defaulting to null
        delivery_instruction: form.getFieldValue("instructions") || null,
        is_requested_cutlery: false, // Defaulting to false
        cutlery_instruction: null, // Defaulting to null
        location_id: form.getFieldValue("branch"),
        products: validOrderItems.map((item) => ({
          product_id: parseInt(item.productId),
          qty: parseInt(item.quantity),
          amount: parseFloat(item.price),
        })),
      };

      // console.log("Order Payload:", orderPayload);

      await createBulkOrder(orderPayload).unwrap();
      message.success("Bulk order created successfully!");
      handleClose();
    } catch (error) {
      console.error("Error creating order:", error);
      message.error(
        error?.data?.message ||
          "Failed to create bulk order. Please try again.",
      );
    }
  };

  const getCustomerLabel = () => {
    const customerValue = form.getFieldValue("customer");
    const customer = customerOptions.find(
      (c) => c.value === parseInt(customerValue),
    );
    return customer?.label || "Not selected";
  };

  const getEquivalent = (item) => {
    return `${item.quantity * item.servingSize} ${item.unit}`;
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <SelectCustomer
            form={form}
            customerLoading={customerLoading}
            customerOptions={customerOptions}
            onAddNewCustomer={() => setIsCreateCustomerOpen(true)}
            searchValue={customerSearch}
            onSearchChange={setCustomerSearch}
          />
        );

      case 1:
        return (
          <SelectProducts
            orderItems={orderItems}
            handleProductSelect={handleProductSelect}
            productsLoading={productsLoading}
            productDropdownOptions={productDropdownOptions}
            handleQuantityChange={handleQuantityChange}
            getEquivalent={getEquivalent}
            removeOrderItem={removeOrderItem}
            addOrderItem={addOrderItem}
            calculateTotal={calculateTotal}
            discount={discount}
            setDiscount={setDiscount}
            discountType={discountType}
            setDiscountType={setDiscountType}
          />
        );
      case 2:
        return (
          <DeliverySection
            // Removed deliveryType and setDeliveryType props
            form={form}
            locationLoading={locationLoading}
            locationOptions={locationOptions}
            // DeliverySection now fetches deliveryModes internally, no need to pass here
          />
        );
      case 3:
        const reviewData = calculateReviewData();
        const validOrderItems = orderItems.filter(
          (item) => item.productId && item.quantity > 0,
        );

        // Get current form values for review
        const formValues = form.getFieldsValue();
        const selectedModeId = formValues.deliveryModeId;
        const selectedDeliveryMode = deliveryModes?.find(
          (mode) => mode.id === selectedModeId,
        );
        const isSelectedModeDelivery = selectedDeliveryMode?.name
          .toLowerCase()
          .includes("delivery");
        const isSelectedModePickup = selectedDeliveryMode?.name
          .toLowerCase()
          .includes("pickup");

        return (
          <ReviewBulkOrder
            reviewData={reviewData}
            validOrderItems={validOrderItems}
            getCustomerLabel={getCustomerLabel}
            getEquivalent={getEquivalent}
            // Pass delivery details to ReviewBulkOrder for display
            deliveryDetails={{
              modeName: selectedDeliveryMode?.name,
              date: isSelectedModeDelivery
                ? formValues.deliveryDate?.format("DD-MM-YYYY")
                : formValues.pickupDate?.format("DD-MM-YYYY"),
              time: isSelectedModeDelivery
                ? formValues.deliveryTime
                : formValues.pickupTime,
              address: isSelectedModeDelivery
                ? formValues.deliveryLocation
                : null, // Only for delivery
              branch: locationOptions.find(
                (loc) => loc.value === formValues.branch,
              )?.label,
              instructions: formValues.instructions,
            }}
          />
        );

      default:
        return null;
    }
  };

  return (
    <>
      <ReusableModal
        open={open}
        onClose={handleClose}
        title="Create Bulk Order"
        subtitle="Kindly fill in the details below to create a bulk order for a customer"
        steps={steps}
        currentStep={currentStep}
        onPrevious={handlePrevious}
        onNext={handleNext}
        onFinish={handleFinish}
        form={form}
        stepStyles={bulkOrderStepStyles}
        stepsClassName="mb-4 w-full mx-auto text-sm"
        position="right"
        slideDirection="right"
        animationDuration={300}
        width={650}
        isLoading={creatingBulkOrder}
        // bodyStyle={{ maxHeight: "calc(100vh - 290px)", overflowY: "auto" }}
      >
        {renderStepContent()}
      </ReusableModal>
      <CreateCustomerModal
        open={isCreateCustomerOpen}
        onClose={() => setIsCreateCustomerOpen(false)}
        onSuccess={(fullName) => {
          setCustomerSearch(fullName);
          setIsCreateCustomerOpen(false);
        }}
      />
    </>
  );
};

export default CreateBulkOrderModal;
